import React from 'react';
import { Share2, Mail, Linkedin, Facebook, Instagram, Link2, Send } from 'lucide-react';
import { analytics } from '../utils/analytics';

interface SocialShareProps {
  url: string;
  title: string;
  description?: string;
  contentId: string;
}

const SocialShare: React.FC<SocialShareProps> = ({ url, title, description, contentId }) => {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description || '');

  const shareLinks = {
    email: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`,
    linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}&summary=${encodedDescription}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    instagram: `https://instagram.com/share?url=${encodedUrl}`,
    tiktok: `https://www.tiktok.com/share?url=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`
  };

  const handleShare = async (platform: string) => {
    await analytics.trackShare({
      contentId,
      platform,
      referrer: document.referrer
    });
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      await analytics.trackShare({
        contentId,
        platform: 'clipboard',
        referrer: document.referrer
      });
      alert('Link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="flex flex-col items-start space-y-4">
      <div className="flex items-center text-gray-600">
        <Share2 className="h-5 w-5 mr-2" />
        <span className="font-medium">Share this article</span>
      </div>
      <div className="flex space-x-4">
        <a
          href={shareLinks.email}
          className="p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          title="Share via Email"
          onClick={() => handleShare('email')}
        >
          <Mail className="h-5 w-5" />
        </a>
        <a
          href={shareLinks.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
          title="Share on LinkedIn"
          onClick={() => handleShare('linkedin')}
        >
          <Linkedin className="h-5 w-5" />
        </a>
        <a
          href={shareLinks.facebook}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
          title="Share on Facebook"
          onClick={() => handleShare('facebook')}
        >
          <Facebook className="h-5 w-5" />
        </a>
        <a
          href={shareLinks.instagram}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 text-pink-600 hover:bg-pink-50 rounded-full transition-colors"
          title="Share on Instagram"
          onClick={() => handleShare('instagram')}
        >
          <Instagram className="h-5 w-5" />
        </a>
        <a
          href={shareLinks.tiktok}
          target="_blank"
          rel="noopener noreferrer"
          className="p-2 text-black hover:bg-gray-50 rounded-full transition-colors"
          title="Share on TikTok"
          onClick={() => handleShare('tiktok')}
        >
          <Send className="h-5 w-5" />
        </a>
        <button
          onClick={copyToClipboard}
          className="p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          title="Copy link"
        >
          <Link2 className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default SocialShare;